import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { 
  XCircle, 
  ArrowClockwise,
  Warning,
  PaperPlaneTilt,
  Eye,
  X,
  FileText,
  CurrencyDollar,
  Clock,
  CheckCircle,
  ArrowRight
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function DenialManagementPage() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ denied: 0, appealed: 0, total_denied_amount: 0 });
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealData, setAppealData] = useState({ appeal_reason: '', additional_notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reasonCodes, setReasonCodes] = useState([]);

  useEffect(() => {
    fetchDenials();
    fetchReasonCodes();
  }, []);

  const fetchDenials = async () => {
    try {
      const response = await api.get('/billing/denials');
      setClaims(response.data.claims || []);
      setStats(response.data.stats || {});
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching denials:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReasonCodes = async () => {
    try {
      const response = await api.get('/billing/denials/reason-codes');
      setReasonCodes(response.data.reason_codes || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching reason codes:', error);
    }
  };

  const submitAppeal = async () => {
    if (!appealData.appeal_reason.trim()) {
      toast.error('Please provide an appeal reason');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/billing/denials/${selectedClaim.id}/appeal`, {
        claim_id: selectedClaim.id,
        ...appealData
      });
      toast.success('Appeal submitted successfully');
      setShowAppealModal(false);
      setAppealData({ appeal_reason: '', additional_notes: '' });
      setSelectedClaim(null);
      fetchDenials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit appeal');
    } finally {
      setSubmitting(false);
    }
  };

  const resubmitClaim = async (claimId) => {
    try {
      const response = await api.post(`/billing/denials/${claimId}/resubmit`);
      toast.success(`Claim resubmitted as ${response.data.new_claim_number}`);
      fetchDenials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit claim');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      denied: 'bg-red-100 text-red-700',
      appealed: 'bg-purple-100 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-100'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-8" data-testid="denial-management-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Denial Management
        </h1>
        <p className="text-slate-500">Track, appeal, and resubmit denied claims</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card-clinical p-4 border-l-4 border-red-500">
          <p className="text-sm text-slate-500">Denied Claims</p>
          <p className="text-3xl font-bold text-red-600">{stats.denied}</p>
        </div>
        <div className="card-clinical p-4 border-l-4 border-purple-500">
          <p className="text-sm text-slate-500">Under Appeal</p>
          <p className="text-3xl font-bold text-purple-600">{stats.appealed}</p>
        </div>
        <div className="card-clinical p-4 border-l-4 border-amber-500">
          <p className="text-sm text-slate-500">Total Denied Amount</p>
          <p className="text-3xl font-bold text-amber-600">{formatCurrency(stats.total_denied_amount)}</p>
        </div>
      </div>

      {/* Claims Table */}
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Claim #</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Insurance</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Amount</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Denial Reason</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading...
                </td>
              </tr>
            ) : claims.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <CheckCircle size={48} className="mx-auto mb-2 text-green-300" />
                  No denied claims - great job!
                </td>
              </tr>
            ) : (
              claims.map((claim) => (
                <tr key={claim.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-teal-600">{claim.claim_number}</span>
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-900">{claim.patient_name}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{claim.insurance_name}</td>
                  <td className="py-3 px-4 text-right font-medium">{formatCurrency(claim.total_charges)}</td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-red-600 truncate max-w-[200px]" title={claim.denial_reason}>
                      {claim.denial_reason || 'Not specified'}
                    </p>
                    {claim.denial_code && (
                      <span className="text-xs text-slate-500">{claim.denial_code}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">{getStatusBadge(claim.status)}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedClaim(claim)}
                        className="p-2 hover:bg-slate-100 rounded-lg"
                        title="View Details"
                      >
                        <Eye size={18} className="text-slate-500" />
                      </button>
                      {claim.status === 'denied' && (
                        <>
                          <button
                            onClick={() => { setSelectedClaim(claim); setShowAppealModal(true); }}
                            className="p-2 hover:bg-purple-100 rounded-lg"
                            title="Submit Appeal"
                          >
                            <FileText size={18} className="text-purple-600" />
                          </button>
                          <button
                            onClick={() => resubmitClaim(claim.id)}
                            className="p-2 hover:bg-teal-100 rounded-lg"
                            title="Resubmit Claim"
                          >
                            <PaperPlaneTilt size={18} className="text-teal-600" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Common Denial Reasons Reference */}
      <div className="mt-6 card-clinical p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Common Denial Reason Codes</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {reasonCodes.slice(0, 9).map((code) => (
            <div key={code.code} className="p-3 bg-slate-50 rounded-lg">
              <span className="font-mono text-sm text-red-600 font-bold">{code.code}</span>
              <p className="text-xs text-slate-600 mt-1">{code.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Appeal Modal */}
      {showAppealModal && selectedClaim && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Submit Appeal</h2>
                <p className="text-slate-500">Claim: {selectedClaim.claim_number}</p>
              </div>
              <button onClick={() => { setShowAppealModal(false); setSelectedClaim(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {selectedClaim.denial_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-red-800">Denial Reason:</p>
                <p className="text-sm text-red-700">{selectedClaim.denial_reason}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Appeal Reason *</label>
              <textarea
                value={appealData.appeal_reason}
                onChange={(e) => setAppealData(prev => ({ ...prev, appeal_reason: e.target.value }))}
                className="input-clinical"
                rows={4}
                placeholder="Explain why this claim should be reconsidered..."
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-2">Additional Notes</label>
              <textarea
                value={appealData.additional_notes}
                onChange={(e) => setAppealData(prev => ({ ...prev, additional_notes: e.target.value }))}
                className="input-clinical"
                rows={2}
                placeholder="Any supporting information..."
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => { setShowAppealModal(false); setSelectedClaim(null); }} className="flex-1 btn-outline">
                Cancel
              </button>
              <button
                onClick={submitAppeal}
                disabled={submitting}
                className="flex-1 btn-teal flex items-center justify-center gap-2"
              >
                {submitting ? <ArrowClockwise size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} />}
                Submit Appeal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim Detail Modal */}
      {selectedClaim && !showAppealModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Claim Details</h2>
                <p className="text-teal-600 font-mono">{selectedClaim.claim_number}</p>
              </div>
              {getStatusBadge(selectedClaim.status)}
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500">Patient</p>
                <p className="font-medium">{selectedClaim.patient_name}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500">Insurance</p>
                <p className="font-medium">{selectedClaim.insurance_name}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500">Total Charges</p>
                <p className="font-bold text-lg">{formatCurrency(selectedClaim.total_charges)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500">Service Date</p>
                <p className="font-medium">{formatDate(selectedClaim.service_date)}</p>
              </div>
            </div>

            {selectedClaim.denial_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-red-800 mb-1">Denial Reason</p>
                <p className="text-red-700">{selectedClaim.denial_reason}</p>
                {selectedClaim.denial_code && (
                  <p className="text-sm text-red-600 mt-2">Code: {selectedClaim.denial_code}</p>
                )}
              </div>
            )}

            {selectedClaim.appeal && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-purple-800 mb-1">Appeal Submitted</p>
                <p className="text-purple-700">{selectedClaim.appeal.appeal_reason}</p>
                <p className="text-xs text-purple-600 mt-2">
                  By {selectedClaim.appeal.submitted_by_name} on {formatDate(selectedClaim.appeal.appeal_date)}
                </p>
              </div>
            )}

            {selectedClaim.resubmitted_claim_number && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
                <p className="font-medium text-teal-800">Resubmitted as: {selectedClaim.resubmitted_claim_number}</p>
              </div>
            )}

            <button onClick={() => setSelectedClaim(null)} className="w-full btn-outline">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
